// 1. React
import { memo, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// 2. Helpers
import { useTranslate } from "helpers";
import { getActiveUser } from "services/api/users";

// 3. Types
import { InvoiceSettingsProps } from "./types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// 4. Form hooks
import { useHandleDropdownChange } from "services/forms/hooks";

// 5. API
import { getLanguagesAPI } from "services/api/finance/lookups/api";
import { getCurrenciesAPI } from "services/api/lookups/api";

// 6. UI Components
import { Dropdown } from "ui/components/Dropdown/Dropdown";


const InvoiceSettings: React.FC<InvoiceSettingsProps> = (props) => {
    const { formValues, setFormValues } = props;

    const { t } = useTranslate();
    const { user } = useSelector(getActiveUser);
    const bankAccounts = user?.tenant?.bankAccounts ?? [];

    // Auto-fill primary IBAN
    useEffect(() => {
        if (formValues.paymentIBAN || bankAccounts.length === 0) return;
        const primary = bankAccounts.find(ba => ba.primary) || bankAccounts[0];
        if (primary) {
            setFormValues(prev => ({ ...prev, paymentIBAN: primary.iban }));
        }
    }, [bankAccounts, formValues.contractSaleId]);

    const ibanOptions = useMemo<DropdownOption[]>(() => {
        return bankAccounts.map((ba) => ({
            value: ba.iban,
            text: (<div className="flex items-center justify-between w-full">
                <span>{ba.iban}</span>
                {ba.bankName && <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">{ba.bankName}</span>}
            </div>),
        }));
    }, [bankAccounts]);

    const [currencyOptions, setCurrencyOptions] = useState<DropdownOption[]>([]);
    const [languageOptions, setLanguageOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
        fetchLookups();
    }, []);

    const fetchLookups = async () => {
        try {
            const [currencies, languages] = await Promise.all([
                getCurrenciesAPI(),
                getLanguagesAPI(),
            ]);

            setCurrencyOptions(currencies?.map((item: any) => ({ text: item.name, value: item.id })) ?? []);
            setLanguageOptions(languages?.map((item: any) => ({ text: item.name, value: item.id, image: `/media/icons/flags/${item.id}.png` })) ?? []);
        } catch {
            // error handled by snackbar
        }
    };

    const handleDropdownChange = useHandleDropdownChange(
        setFormValues,
        formValues
    );

    return (
        <div className="flex flex-col gap-3">
            <div className="flex gap-4">
                <div className="flex-1">
                    <Dropdown
                        name="languageId"
                        placeholder={t("admin.fleet.vehicle.invoice.add.language.placeholder")}
                        label={t("admin.fleet.vehicle.invoice.add.language")}
                        options={languageOptions}
                        onChange={handleDropdownChange}
                        value={formValues.languageId}
                    />
                </div>
                <div className="flex-1">
                    <Dropdown
                        name="currencyId"
                        placeholder={t("admin.fleet.vehicle.invoice.add.currency.placeholder")}
                        label={t("admin.fleet.vehicle.invoice.add.currency")}
                        options={currencyOptions}
                        onChange={handleDropdownChange}
                        value={formValues.currencyId}
                    />
                </div>
                <div className="flex-1">
                    <Dropdown
                        label={t("admin.fleet.vehicle.invoice.add.paymentiban")}
                        name="paymentIBAN"
                        options={ibanOptions}
                        onChange={handleDropdownChange}
                        value={formValues.paymentIBAN}
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(InvoiceSettings);
